import { FC } from 'react';
import Modal from '@new/common/ui/Modal/Modal';
import { Button } from '@new/common/ui';

interface ConfirmSwitchDegreeModalProps {
  isOpen: boolean;
  fromDegreeName?: string;
  toDegreeName: string;
  changesSubscription: boolean;
  toIsMaster: boolean;
  isPending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

// Admin confirmation for switching a student's degree (+ subscription plan).
export const ConfirmSwitchDegreeModal: FC<ConfirmSwitchDegreeModalProps> = ({
  isOpen,
  fromDegreeName,
  toDegreeName,
  changesSubscription,
  toIsMaster,
  isPending,
  onConfirm,
  onClose,
}) => {
  const targetPlanLabel = toIsMaster ? "master's plan" : 'standard monthly plan';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Switch degree" className="h-auto w-130">
      <div className="flex flex-col gap-5 py-2">
        <div className="flex flex-col gap-2">
          <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-p2)]! leading-[150%] text-(--neutral-black-NB1000)! m-0!">
            {fromDegreeName ? (
              <>
                This student is enrolled in <strong>{fromDegreeName}</strong>. Switching moves them
                to <strong>{toDegreeName}</strong> and removes the old degree.
              </>
            ) : (
              <>
                This will enroll the student in <strong>{toDegreeName}</strong>.
              </>
            )}
          </p>

          {changesSubscription && (
            <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-p3)]! leading-[150%] text-(--danger-D700)! font-semibold m-0!">
              Because {toDegreeName} crosses the master&apos;s boundary, the student&apos;s paid
              subscription will be moved to the {targetPlanLabel} to match.
            </p>
          )}

          <p className="[font-size:var(--fs-p3)]! leading-[150%] text-(--neutral-black-NB700)! m-0!">
            The student keeps progress on any started courses. This is an administrative override
            and won&apos;t be undone automatically — switch back manually if needed.
          </p>
        </div>

        <div className="flex w-full items-center justify-end gap-3 pt-1">
          <Button variant="neutral" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
            className="border-2! border-(--danger-D700)! bg-(--danger-D700)! text-(--neutral-white-NW1000)!"
          >
            {isPending ? 'Switching...' : 'Yes, switch degree'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
